import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  productWorkspaceApi,
  type KPIValue,
  type ProductNote,
  type ProductWorkspace,
  type QuickAction,
  type SearchResult,
  type TimelineEvent,
  type WorkspaceRecommendation,
  type WorkspaceTab,
} from '../../api/product-workspace-asin360';

type Theme = 'light' | 'dark';
type CenterView = string;

const DEFAULT_ASIN = 'B0CX123456';

const priorityCls = (p: string) =>
  p === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    : p === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    : p === 'medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

function Skeleton({ className = 'h-20' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 ${className}`} />;
}

export function ProductWorkspacePage() {
  const { asin: routeAsin } = useParams<{ asin: string }>();
  const asin = routeAsin ?? DEFAULT_ASIN;

  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [data, setData] = useState<ProductWorkspace | null>(null);
  const [tabs, setTabs] = useState<WorkspaceTab[]>([]);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [activeTab, setActiveTab] = useState<CenterView>('overview');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-indigo-900/40 bg-zinc-900' : 'border-indigo-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-indigo-50/20 text-slate-900';
  const accent = dark ? 'text-indigo-300' : 'text-indigo-700';
  const tabActive = 'bg-indigo-600 text-white';

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [ws, nav, actions] = await Promise.all([
        productWorkspaceApi.workspace(asin),
        productWorkspaceApi.navigation(),
        productWorkspaceApi.quickActions(asin),
      ]);
      setData(ws);
      setTabs(nav);
      setQuickActions(actions);
      setActiveTab(ws.active_tab || 'overview');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load product workspace');
    } finally {
      setLoading(false);
    }
  }, [asin]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!searchOpen || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      productWorkspaceApi.search(searchQuery).then(setSearchResults).catch(() => setSearchResults([]));
    }, 150);
    return () => clearTimeout(t);
  }, [searchQuery, searchOpen]);

  const filteredTimeline = useMemo(() => {
    const tl = data?.timeline ?? [];
    if (timelineFilter === 'all') return tl;
    return tl.filter((e) => e.type === timelineFilter);
  }, [data?.timeline, timelineFilter]);

  const header = data?.header;

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main" aria-label={`Product workspace ${asin}`}>
      {/* Product Header */}
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start gap-4">
          {header?.image_url && (
            <img src={header.image_url} alt="" className="h-16 w-16 rounded-lg border object-cover" loading="lazy" />
          )}
          <div className="min-w-0 flex-1">
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000104 · ASIN 360 · Product Operating System</p>
            <h1 className="truncate text-xl font-semibold">{header?.product_name ?? asin}</h1>
            <div className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm ${muted}`}>
              <span><strong className={accent}>ASIN</strong> {header?.asin ?? asin}</span>
              {header?.parent_asin && <span>Parent {header.parent_asin}</span>}
              <span>SKU {header?.sku}</span>
              <span>{header?.marketplace}</span>
              <span>{header?.brand}</span>
              <span>{header?.category}</span>
              <span className={header?.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}>{header?.status}</span>
              {header?.compliance_status && <span>Compliance: {header.compliance_status}</span>}
              {header?.business_owner && <span>Owner: {header.business_owner}</span>}
            </div>
            {header?.badges && header.badges.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {header.badges.map((b) => (
                  <span key={b} className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{b}</span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setSearchOpen(true)} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`} aria-label="Search products">
              Search ⌘K
            </button>
            <button type="button" className={`rounded-lg border px-3 py-1.5 text-sm ${card}`} aria-pressed={header?.bookmarked}>
              {header?.bookmarked ? '★ Bookmarked' : '☆ Bookmark'}
            </button>
            <button type="button" className={`rounded-lg border px-3 py-1.5 text-sm ${card}`} aria-pressed={header?.favorited}>
              {header?.favorited ? '♥ Favorite' : '♡ Favorite'}
            </button>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
              {dark ? 'Light' : 'Dark'}
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>
      )}

      {/* KPI Ribbon */}
      <section aria-label="Business KPI ribbon">
        <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-11">
          {loading ? Array.from({ length: 11 }).map((_, i) => <Skeleton key={i} className="h-16" />)
            : data?.kpis.map((k) => <KPICard key={k.key} kpi={k} card={card} muted={muted} accent={accent} />)}
        </div>
      </section>

      {/* Main workspace: left nav · center · AI panel */}
      <div className="grid gap-4 lg:grid-cols-12">
        <nav className="lg:col-span-2" aria-label="Workspace navigation">
          <div className={`rounded-xl border p-2 ${card}`}>
            <p className={`mb-2 px-2 text-xs font-semibold uppercase ${muted}`}>Workspace</p>
            <ul className="space-y-0.5">
              {(tabs.length ? tabs : [{ key: 'overview', name: 'Overview', route: 'overview', status: 'active' }]).map((t) => (
                <li key={t.key}>
                  <button
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${activeTab === t.key ? tabActive : `hover:bg-indigo-50 dark:hover:bg-indigo-950/40`}`}
                    aria-current={activeTab === t.key ? 'page' : undefined}
                  >
                    {t.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="space-y-4 lg:col-span-7">
          {activeTab === 'overview' && (
            <OverviewPanel data={data} loading={loading} card={card} muted={muted} accent={accent} priorityCls={priorityCls} />
          )}
          {activeTab === 'marketplace_dna' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>Marketplace DNA</p>
              <p className={`mt-2 text-sm ${muted}`}>Full DNA profile powered by DEV-000105 Marketplace DNA Engine.</p>
              <Link to={`/products/${asin}/dna`} className={`mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500`}>Open DNA Profile →</Link>
            </div>
          )}
          {activeTab === 'listing' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>Listing Growth Intelligence</p>
              <p className={`mt-2 text-sm ${muted}`}>Health scoring, DNA-backed recommendations, simulation and experiments powered by DEV-000106.</p>
              <Link to={`/products/${asin}/listing-growth`} className={`mt-4 inline-block rounded-lg bg-amber-600 px-4 py-2 text-sm text-white hover:bg-amber-500`}>Open Listing Growth →</Link>
            </div>
          )}
          {activeTab === 'advertising' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>Advertising Growth Intelligence</p>
              <p className={`mt-2 text-sm ${muted}`}>Campaign health, bid/budget intelligence, simulation and attribution powered by DEV-000107.</p>
              <Link to="/advertising/CAMP-SP-001/growth" className={`mt-4 inline-block rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-500`}>Open Advertising Growth →</Link>
            </div>
          )}
          {activeTab === 'inventory' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>FBA Growth Intelligence</p>
              <p className={`mt-2 text-sm ${muted}`}>Inventory health, demand forecast, replenishment and shipment planning powered by DEV-000108.</p>
              <Link to={`/fba/${asin}/growth`} className={`mt-4 inline-block rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-500`}>Open FBA Growth →</Link>
            </div>
          )}
          {activeTab === 'profit' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>Profit Intelligence</p>
              <p className={`mt-2 text-sm ${muted}`}>True profit, leakage detection, settlement reconciliation and recovery powered by DEV-000109.</p>
              <Link to={`/profit/${asin}/intelligence`} className={`mt-4 inline-block rounded-lg bg-amber-600 px-4 py-2 text-sm text-white hover:bg-amber-500`}>Open Profit Intelligence →</Link>
            </div>
          )}
          {activeTab !== 'overview' && activeTab !== 'marketplace_dna' && activeTab !== 'listing' && activeTab !== 'advertising' && activeTab !== 'inventory' && activeTab !== 'profit' && (
            <div className={`rounded-xl border p-8 text-center ${card}`}>
              <p className={`text-lg font-semibold ${accent}`}>{tabs.find((t) => t.key === activeTab)?.name ?? activeTab}</p>
              <p className={`mt-2 text-sm ${muted}`}>Integration-ready tab — provider-driven content plugs in without structural redesign.</p>
              <Link to={`/platform/product-workspace-asin360-dev104`} className={`mt-4 inline-block text-sm ${accent} hover:underline`}>View DEV-000104 sprint center →</Link>
            </div>
          )}

          {/* Quick Actions */}
          <section aria-label="Quick actions">
            <h2 className="mb-2 text-sm font-semibold">Quick Actions</h2>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((a) => (
                <Link key={a.key} to={routeForCommand(a.command, asin)} className={`rounded-lg border px-3 py-1.5 text-sm font-medium hover:opacity-90 ${card} ${accent}`}>
                  {a.name}
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* AI Panel */}
        <aside className="lg:col-span-3" aria-label="AI workspace">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="text-sm font-semibold">AI Workspace</h2>
            <p className={`mt-1 text-xs ${muted}`}>Framework only — no AI logic this sprint</p>
            {data?.ai_frame && (
              <div className={`mt-3 space-y-2 text-sm ${muted}`}>
                <p>Context: {data.ai_frame.context_asin}</p>
                <p>Conversation: {data.ai_frame.conversation_enabled ? 'Ready' : 'Disabled'}</p>
                <p>Evidence panel: {data.ai_frame.evidence_enabled ? 'Ready' : 'Disabled'}</p>
                {data.ai_frame.placeholder_actions?.map((a) => (
                  <span key={a} className="mr-1 inline-block rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{a}</span>
                ))}
              </div>
            )}
            <div className={`mt-4 rounded-lg border border-dashed p-4 text-center text-xs ${muted}`}>
              Conversation · Evidence · Recommendations · DNA context
            </div>
          </div>

          <div className={`mt-4 rounded-xl border p-4 ${card}`}>
            <h2 className="text-sm font-semibold">Knowledge Graph</h2>
            {data?.knowledge_frame && (
              <div className={`mt-2 text-xs ${muted}`}>
                <p>Nodes: {data.knowledge_frame.node_types.join(', ')}</p>
                <p className="mt-1">Relations: {data.knowledge_frame.relationship_types.join(', ')}</p>
              </div>
            )}
            <div className={`mt-3 rounded-lg border border-dashed p-6 text-center text-xs ${muted}`}>
              Visualization framework placeholder
            </div>
          </div>
        </aside>
      </div>

      {/* Timeline */}
      <section aria-label="Activity timeline" className={`rounded-xl border p-4 ${card}`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Activity Timeline</h2>
          <div className="flex flex-wrap gap-1">
            {['all', 'listing', 'advertising', 'inventory', 'profit', 'system', 'user'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setTimelineFilter(f)}
                className={`rounded px-2 py-0.5 text-xs capitalize ${timelineFilter === f ? tabActive : `border ${card}`}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-64 space-y-2 overflow-y-auto" role="log">
          {loading ? <Skeleton /> : filteredTimeline.length === 0 ? (
            <p className={`text-sm ${muted}`}>No timeline events for this filter.</p>
          ) : filteredTimeline.map((e) => (
            <TimelineRow key={e.id} event={e} muted={muted} />
          ))}
        </div>
      </section>

      {searchOpen && (
        <SearchOverlay
          dark={dark}
          card={card}
          muted={muted}
          accent={accent}
          query={searchQuery}
          results={searchResults}
          onQuery={setSearchQuery}
          onClose={() => setSearchOpen(false)}
        />
      )}
    </div>
  );
}

function KPICard({ kpi, card, muted, accent }: { kpi: KPIValue; card: string; muted: string; accent: string }) {
  const inner = (
    <div className={`rounded-lg border p-3 transition hover:shadow-sm ${card}`} title={kpi.benchmark ? `Benchmark: ${kpi.benchmark}` : undefined}>
      <p className={`truncate text-[10px] uppercase ${muted}`}>{kpi.name}</p>
      <p className="mt-0.5 text-lg font-semibold">{kpi.value}{kpi.unit && <span className={`ml-0.5 text-xs font-normal ${muted}`}>{kpi.unit}</span>}</p>
      {kpi.trend && (
        <p className={`text-[10px] ${kpi.trend.startsWith('+') ? 'text-emerald-600' : kpi.trend.startsWith('-') ? 'text-red-600' : muted}`}>{kpi.trend}</p>
      )}
    </div>
  );
  return kpi.drill_down ? <Link to={kpi.drill_down}>{inner}</Link> : inner;
}

function OverviewPanel({
  data, loading, card, muted, accent, priorityCls,
}: {
  data: ProductWorkspace | null; loading: boolean; card: string; muted: string; accent: string;
  priorityCls: (p: string) => string;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className={`rounded-xl border p-4 ${card}`}>
          <h2 className="text-sm font-semibold">Business Summary</h2>
          {loading ? <Skeleton className="mt-2 h-12" /> : (
            <p className={`mt-2 text-sm ${muted}`}>
              Every ASIN is a business — identity, health, performance, growth, risks and recommendations in one operating environment.
            </p>
          )}
        </div>
        <div className={`rounded-xl border p-4 ${card}`}>
          <h2 className="text-sm font-semibold">Marketplace Health</h2>
          {loading ? <Skeleton className="mt-2 h-12" /> : (
            <p className={`mt-2 text-2xl font-semibold ${accent}`}>
              {data?.kpis.find((k) => k.key === 'marketplace_health')?.value ?? '—'}
            </p>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">Recommendations</h2>
        <div className="space-y-2">
          {loading ? <Skeleton /> : (data?.recommendations ?? []).map((r) => (
            <RecommendationCard key={r.id} rec={r} card={card} muted={muted} accent={accent} priorityCls={priorityCls} />
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">Notes</h2>
        <div className="space-y-2">
          {loading ? <Skeleton /> : (data?.notes ?? []).length === 0 ? (
            <p className={`text-sm ${muted}`}>No notes yet.</p>
          ) : data?.notes.map((n) => (
            <NoteRow key={n.id} note={n} card={card} muted={muted} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RecommendationCard({
  rec, card, muted, accent, priorityCls,
}: {
  rec: WorkspaceRecommendation; card: string; muted: string; accent: string; priorityCls: (p: string) => string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${card}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(rec.priority)}`}>{rec.priority}</span>
          <h3 className="mt-1 font-semibold">{rec.title}</h3>
          <p className={`text-sm ${muted}`}>{rec.business_impact}</p>
        </div>
        <div className={`text-right text-xs ${muted}`}>
          <p>Confidence {(rec.confidence * 100).toFixed(0)}%</p>
          {rec.expected_revenue && <p>Rev {rec.expected_revenue}</p>}
          {rec.expected_profit && <p>Profit {rec.expected_profit}</p>}
        </div>
      </div>
      {rec.evidence && rec.evidence.length > 0 && (
        <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{rec.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
      )}
      <div className="mt-3 flex gap-2">
        <Link to={rec.action_path} className={`rounded-lg bg-indigo-600 px-3 py-1.5 text-xs text-white hover:bg-indigo-500`}>{rec.action_label}</Link>
        <span className={`self-center text-xs ${muted}`}>{rec.status}</span>
      </div>
    </div>
  );
}

function NoteRow({ note, card, muted }: { note: ProductNote; card: string; muted: string }) {
  return (
    <div className={`rounded-lg border p-3 ${card}`}>
      <div className="flex items-center gap-2 text-xs">
        {note.pinned && <span className="text-amber-500">📌</span>}
        {note.private && <span className={`${muted}`}>Private</span>}
        <span className={muted}>{note.author}</span>
      </div>
      <p className="mt-1 text-sm">{note.content}</p>
    </div>
  );
}

function TimelineRow({ event, muted }: { event: TimelineEvent; muted: string }) {
  return (
    <div className="flex gap-3 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
      <time className={`shrink-0 text-xs ${muted}`}>{new Date(event.timestamp).toLocaleString()}</time>
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase dark:bg-slate-800">{event.type}</span>
      <span className="text-sm">{event.title}</span>
      {event.actor && <span className={`text-xs ${muted}`}>— {event.actor}</span>}
    </div>
  );
}

function SearchOverlay({
  dark, card, muted, accent, query, results, onQuery, onClose,
}: {
  dark: boolean; card: string; muted: string; accent: string;
  query: string; results: SearchResult[]; onQuery: (q: string) => void; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[15vh]" role="dialog" aria-modal="true" aria-label="Product search">
      <div className={`w-full max-w-lg rounded-xl border p-4 shadow-xl ${dark ? 'bg-zinc-900' : 'bg-white'}`}>
        <input
          autoFocus
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search ASIN, SKU, keyword…"
          className={`w-full rounded-lg border px-3 py-2 text-sm ${card}`}
          aria-label="Search query"
        />
        <ul className="mt-2 max-h-64 overflow-y-auto">
          {results.map((r) => (
            <li key={r.key}>
              <Link to={r.path} onClick={onClose} className={`block rounded-lg px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-950/40`}>
                <span className={accent}>{r.label}</span>
                <span className={`ml-2 text-xs ${muted}`}>{r.type}</span>
              </Link>
            </li>
          ))}
          {query.length >= 2 && results.length === 0 && <li className={`px-3 py-2 text-sm ${muted}`}>No results</li>}
        </ul>
        <button type="button" onClick={onClose} className={`mt-2 text-xs ${muted}`}>Esc to close</button>
      </div>
    </div>
  );
}

function routeForCommand(command: string, asin: string): string {
  const map: Record<string, string> = {
    'open-listing': `/products/${asin}/workspace?tab=listing`,
    'open-advertising': `/products/${asin}/workspace?tab=advertising`,
    'open-inventory': `/products/${asin}/workspace?tab=inventory`,
    'open-profit': `/products/${asin}/workspace?tab=profit`,
    'create-note': `/products/${asin}/workspace?tab=notes`,
    'bookmark': `/products/${asin}/workspace`,
    'export': `/products/${asin}/workspace?tab=documents`,
    'generate-report': `/products/${asin}/workspace`,
  };
  return map[command] ?? `/products/${asin}/workspace`;
}
